import { createApiRequest } from "../common";

/**
 * UC-CAL-2 — spawn a Recall.ai meeting bot from a calendar event.
 *
 * The api-server route `/calendar/create-bot` already exists (see
 * `equal-iq-ea/src/api-server/routes/oauth.ts:140`). It looks up the
 * Nylas calendar event by id, extracts the meeting link (Zoom / Meet /
 * Teams), and dispatches a Recall.ai bot to join. The frontend just
 * needs a thin client + a CTA — this file is the client; EventSheet
 * holds the CTA.
 */

export interface CreateBotFromCalendarRequest {
  /**
   * Nylas calendar event id (the same `id` field surfaced by
   * listIntegrationCalendarEventsApi). NOT the local `event.id` on the
   * mock calendar — pass through the real Nylas event id.
   */
  eventId: string;
  /** Optional human-readable label that appears in the Recall.ai bot list. */
  botName?: string;
  /** When true, the backend schedules a follow-up bot for the next recurrence. */
  scheduleNextRecurrence?: boolean;
}

export interface CreateBotFromCalendarResponse {
  /** The dispatched bot's id — useful for polling status. */
  botId: string;
  /** When `scheduleNextRecurrence` was true and a next instance was scheduled. */
  nextBotScheduled?: boolean;
  /** Human-readable status the UI can surface in a toast. */
  message?: string;
}

export const createBotFromCalendarApi = async (
  request: CreateBotFromCalendarRequest,
): Promise<CreateBotFromCalendarResponse> => {
  const apiRequest = createApiRequest<CreateBotFromCalendarResponse>(
    'post',
    '/calendar/create-bot',
  );
  return apiRequest(request);
};
